import React, { useState, useRef } from 'react';
import Button from '@mui/material/Button';
import { uploadAllocation } from '../../../services/asset.service';

/* ─── Same CSS as AssetInfo (Bulk Allocation) ─── */
import '../../ExcelUploads/AssetInfo/AssetInfo';
import InfoIcon from '@mui/icons-material/Info';

const internalTemplatePath = "/exceltemplates/Asset_Allocation_Template.xlsx";
const externalTemplatePath = "/exceltemplates/Asset_Allocation_Template.xlsx";
const download_img_Url = "/new-download.gif";

/* ── SVG Download Icon ── */
const DownloadSVG = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
    </svg>
);

/* ── Loading Overlay ── */
const LoadingOverlay = () => (
    <div className="loading-overlay">
        <div className="loading-card">
            <div className="loading-spinner">
                <img src={download_img_Url} alt="Uploading" />
            </div>
            <h5 className="loading-title">Uploading Transfer…</h5>
            <p className="loading-subtitle">
                <i className="bi bi-info-circle-fill" />
                Please don&apos;t refresh the page until the process is completed
            </p>
        </div>
    </div>
);

function InitiateBulkTransfer() {
    const errRef = useRef();
    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    /* ─── Instructions data ─── */
    const instructions = [
        { title: 'File Format:', body: <>Only <code>.xlsx</code> Excel files are accepted</> },
        { title: 'Date Format:', body: <>Always use <code>YYYY-MM-DD</code> format for dates</> },
        { title: 'Empty Cells:', body: <>Use <code>NA</code> for text and <code>0</code> for numbers</> },
        { title: 'Company Name:', body: <><code>{sessionStorage.getItem('LoggedInCompanyName')}</code></> },
        { title: 'Branch Name:', body: <><code>{sessionStorage.getItem('LoggedInDivisionName')}</code></> },
        { title: 'Note:', body: <>Do not close or refresh the page while upload is in progress</> },
    ];

    /* ════════════════════════════
       RENDER
       ════════════════════════════ */
    return (
        <div className="asset-info-container">

            {/* ── Loading Overlay ── */}
            {loading && <LoadingOverlay />}

            {/* ════════════════════════════
                PAGE HEADER
                ════════════════════════════ */}
            <div className="page-header">
                <div className="header-content">
                    <div className="header-left">
                        <div className="header-icon">
                            <i className="bi bi-arrow-left-right" style={{ fontSize: '1.6rem' }} />
                        </div>
                        <div>
                            <h1 className="page-title">Initiate Bulk Transfer</h1>
                            <p className="page-subtitle">Upload Excel files to initiate internal or external transfers in bulk</p>
                        </div>
                    </div>
                </div>

                {/* Alert banners */}
                {success ? (
                    <div className="alert-success-modern" role="alert">
                        <i className="bi bi-check-circle-fill" /> {success}
                    </div>
                ) : errMsg ? (
                    <div className="alert-danger-modern" role="alert" ref={errRef}>
                        <i className="bi bi-exclamation-triangle-fill" /> {errMsg}
                    </div>
                ) : null}
            </div>

            {/* ════════════════════════════
                UPLOAD CARDS
                ════════════════════════════ */}
            <div className="upload-cards-grid">

                {/* Internal Transfer */}
                <div className="upload-card parent-card" id="internal-transfer-upload-card">
                    <div className="card-header">
                        <div className="card-icon parent-icon">
                            <i className="bi bi-building" style={{ fontSize: '1.4rem' }} />
                        </div>
                        <div className="card-header-text">
                            <h4>Internal Transfer Upload</h4>
                            <p>Upload Excel file to initiate internal transfers</p>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="upload-area" id="internal-upload-area">
                            <Button
                                variant="contained"
                                component="label"
                                className="upload-button"
                                id="internal-upload-btn"
                            >
                                <i className="bi bi-cloud-upload" style={{ marginRight: 8 }} />
                                Upload Internal Transfer Excel
                                <input hidden type="file" />
                            </Button>
                        </div>
                        <div className="template-download">
                            <a href={internalTemplatePath} className="download-link" id="internal-template-link">
                                <DownloadSVG /> Download Internal Transfer Template
                            </a>
                        </div>
                    </div>
                </div>

                {/* External Transfer */}
                <div className="upload-card child-card" id="external-transfer-upload-card">
                    <div className="card-header">
                        <div className="card-icon child-icon">
                            <i className="bi bi-globe2" style={{ fontSize: '1.4rem' }} />
                        </div>
                        <div className="card-header-text">
                            <h4>External Transfer Upload</h4>
                            <p>Upload Excel file to initiate external transfers</p>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="upload-area" id="external-upload-area">
                            <Button
                                variant="contained"
                                component="label"
                                className="upload-button"
                                id="external-upload-btn"
                            >
                                <i className="bi bi-cloud-upload" style={{ marginRight: 8 }} />
                                Upload External Transfer Excel
                                <input hidden type="file" />
                            </Button>
                        </div>
                        <div className="template-download">
                            <a href={externalTemplatePath} className="download-link" id="external-template-link">
                                <DownloadSVG /> Download External Transfer Template
                            </a>
                        </div>
                    </div>
                </div>

            </div>{/* /upload-cards-grid */}

            {/* ════════════════════════════
                INSTRUCTIONS
                ════════════════════════════ */}
            <div className="instructions-section" id="instructions-section">
                <div className="instructions-header">
                    <InfoIcon style={{ fontSize: '1.5rem', color: '#0084ff' }} />
                    <h4>Instructions for Bulk Transfer</h4>
                </div>
                <div className="instructions-grid">
                    {instructions.map((item, idx) => (
                        <div className="instruction-item" key={idx} id={`instruction-item-${idx + 1}`}>
                            <div className="instruction-number">{idx + 1}</div>
                            <div className="instruction-content">
                                <strong>{item.title}</strong> {item.body}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
}

export default InitiateBulkTransfer;
